import { db } from "../firebase"; // Adjust path based on your project
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { sendTextMessage } from "../Helpers";

/**
 * Creates a new client in the "clients" collection.
 * @param {string} phone - Digits-only phone number.
 * @param {Object} data - Additional client data (optional).
 */
export const createNewClient = async (phone, data = {}) => {
  try {
    await setDoc(doc(db, "clients", phone), {
      adminNotes: "", // Default empty string
      dob: "", // Default empty string
      email: "", // Default empty string
      firstName: "", // Default empty string
      hasBeenTexted: false, // Default false
      language: "", // Default empty string
      lastName: "", // Default empty string
      menstrual: "", // Default empty string
      notes: "", // Default empty string
      phone,
      isStaff: false, // Will always be false
      unableToAct: false, // Default false
      unableToSchedule: false, // Default false
      verified: false, // Default false
      ...data, // Spread in any additional data passed
    });
  } catch (error) {
    console.error("Error creating client:", error);
    throw error;
  }
};

/**
 * Updates an existing client.
 * @param {string} phone - Digits-only phone number.
 * @param {Object} updates - Fields to update.
 */
export const updateClient = async (phone, updates) => {
  try {
    const clientRef = doc(db, "clients", phone);
    await updateDoc(clientRef, {
      ...updates,
    });
  } catch (error) {
    console.error("Error updating client:", error);
    throw error;
  }
};

export const formatPhoneNumber = (phone) => {
  return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
};

export const sendConfidentialityDisclaimer = async (phone, language) => {
  try {
    await sendTextMessage({
      message: language === "Spanish"
        ? `JMJPC CITA: JMJ Pregnancy Center no divulgará información confidencial de salud por texto. Si usted no es el destinatario previsto de este mensaje, por favor elimínelo. La comunicación por texto no garantiza privacidad completa.`
        : `JMJPC BOOKING: JMJ Pregnancy Center will not disclose confidential health information over text. If you are not the intended recipient of this message, please delete it. Text communication does not guarantee full privacy.`,
      yourPhoneNumber: "4073059084",
      recipientPhoneNumber: phone,
      senderName: "JMJPC"
    });

  } catch (error) {
    console.error("Error sending confidentiality disclaimer or updating client:", error);
    throw error;
  }
};

/**
 * Retrieves all clients whose document ID ends with "STAFF".
 * This does client-side filtering because Firestore doesn't support "endsWith" on document IDs in queries.
 */
export const getStaffUsers = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'clients'));
    const staffUsers = snapshot.docs
      .filter(docSnap => docSnap.id.endsWith('STAFF'))
      .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    return staffUsers;
  } catch (error) {
    console.error('Error fetching staff users:', error);
    throw error;
  }
};

/**
 * Retrieves all staff clients with SMSlist === true (IDs ending with "STAFF" and SMSlist flag).
 */
export const getStaffSMSList = async () => {
  try {
    const snapshot = await getDocs(collection(db, 'clients'));
    const staffWithSMS = snapshot.docs
      .filter(docSnap => docSnap.id.endsWith('STAFF') && docSnap.data()?.SMSlist)
      .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    return staffWithSMS;
  } catch (error) {
    console.error('Error fetching staff SMS list users:', error);
    throw error;
  }
};

/**
 * Updates the SMS notification group setting.
 * @param {'developers' | 'staff'} group
 */
export const updateSMSNotificationGroup = async (group) => {
  try {
    const settingsRef = doc(db, 'settings', 'SMS_GROUP');
    await setDoc(settingsRef, { group }, { merge: true });
  } catch (error) {
    console.error('Error updating SMS notification group:', error);
    throw error;
  }
};

/**
 * Finds a client document by verification token.
 * @param {string} token
 * @returns {Promise<{id: string, data: any} | null>}
 */
export const findClientByVerificationToken = async (token) => {
  if (!token) return null;
  const q = query(
    collection(db, 'clients'),
    where('verificationToken', '==', token)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const docSnap = snapshot.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
};

/**
 * Marks the client as verified and optionally clears the token.
 * @param {string} clientId
 * @param {boolean} clearToken - whether to remove the verificationToken after verifying
 */
export const markClientVerified = async (clientId, clearToken = true) => {
  const updatePayload = { verified: true };
  if (clearToken) updatePayload.verificationToken = null;
  await updateDoc(doc(db, 'clients', clientId), updatePayload);
};